import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser, LoginInput } from '@seenaly/shared';
import { apiFetch, ApiError, SESSION_EXPIRED_EVENT } from '@/lib/api';

/**
 * Session state for the SPA.
 *
 * The session itself is an httpOnly cookie the JavaScript cannot read
 * (docs/spec.md §8.6), so "am I signed in?" is answered by asking the server
 * once on boot rather than by inspecting storage. That also means a session
 * revoked by an admin takes effect on the next reload, not whenever a token
 * happens to expire.
 */

type Status = 'loading' | 'authenticated' | 'anonymous';

interface Auth {
  status: Status;
  user: AuthUser | null;
  /** True from the moment a request reveals the session is gone (idle
   * timeout or admin-revoked) until the user dismisses the popup asking
   * them to sign in again — see docs/spec.md §14 round 3, E12. */
  sessionExpired: boolean;
  acknowledgeSessionExpired: () => void;
  login: (input: LoginInput) => Promise<void>;
  /** Adopts a session established some other way — currently a passkey (G2). */
  adoptSession: (user: AuthUser) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await apiFetch<AuthUser>('/auth/me');
      setUser(me);
      setStatus('authenticated');
    } catch (error) {
      // 401 is the normal "not signed in" answer, not a failure worth
      // surfacing. Anything else still leaves the app usable at the login
      // screen, so it is logged rather than thrown.
      if (!(error instanceof ApiError) || error.code !== 'UNAUTHENTICATED') {
        console.error('[auth] could not load session', error);
      }
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Any request rejected for a dead session flips the app to anonymous, which
  // makes RequireAuth redirect to login with the current page remembered —
  // and raises the popup so that redirect isn't silent (E12).
  useEffect(() => {
    function onExpired() {
      setUser(null);
      setStatus('anonymous');
      setSessionExpired(true);
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const acknowledgeSessionExpired = useCallback(() => setSessionExpired(false), []);

  const login = useCallback(async (input: LoginInput) => {
    // Errors propagate so the form can show the reason; the ApiError code is
    // what the UI translates.
    const me = await apiFetch<AuthUser>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    setUser(me);
    setStatus('authenticated');
    setSessionExpired(false);
  }, []);

  const adoptSession = useCallback((me: AuthUser) => {
    setUser(me);
    setStatus('authenticated');
    setSessionExpired(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } finally {
      // Sign out locally even if the request failed — leaving a stale session
      // on screen after someone pressed "log out" is worse than a server-side
      // session lingering until it expires.
      setUser(null);
      setStatus('anonymous');
      setSessionExpired(false);
    }
  }, []);

  const value = useMemo<Auth>(
    () => ({
      status,
      user,
      sessionExpired,
      acknowledgeSessionExpired,
      login,
      adoptSession,
      logout,
      refresh,
    }),
    [status, user, sessionExpired, acknowledgeSessionExpired, login, adoptSession, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
